import { useIntl } from 'react-intl'
import HttpStatus from 'components/Common/HttpStatus'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import { cloneElement, createElement, isValidElement, useLayoutEffect, useMemo } from 'react'
import type { ComponentType, ReactElement, ReactNode } from 'react'
import { connect } from 'react-redux'
import { matchRoutes, Route as RouterRoute, Routes as RouterRoutes, useLocation, useParams } from 'react-router-dom'
import loadable from 'components/utils/lazyImport'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE_SLUG,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
} from 'constants/offer'
import { TYPE_OF_PAGE_HOLIDAY } from 'constants/landingPage'
import { isCoverGeniusEnabled } from 'lib/insurance/insuranceHelpers'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import TripPlannerRoutes from 'tripPlanner/TripPlannerRoutes'
import { LUXURY_ESCAPES } from 'constants/brands'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { LUX_PLUS_MONTH_LANDING_PAGE_URL } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import LuxPlusMemberRoute from 'luxPlus/components/Routes/LuxPlusMemberRoute'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import AgentHubGuestPageRoutes from 'agentHub/routes/AgentHubGuestPageRoutes'
import { LE_AGENT_HUB_SOCIETE_CONFIRMATION } from 'agentHub/routes/AgentHubRoutes'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { LoadableComponent } from '@loadable/component'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import type { AppRouteMatch } from './routeTypes'
import { matchDestinationProductDotRoute } from 'lib/offer/matchDestinationProductDotRoute'
import { WEDDING_GIFT_REGISTRY_CART_ROUTE } from 'weddings/constants/giftRegistry'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { LOYALTY_INFINITE_ONBOARDING_PAGE, LOYALTY_INFINITE_REGISTER_INTEREST_PAGE, LOYALTY_LANDING_PAGE, LOYALTY_OFFERS_HUB_PAGE, LOUNGE_PASSES_PAGE, LOYALTY_PURCHASE_POINTS_PAGE, LOYALTY_REWARDS_PAGE, LOYALTY_SPEND_POINTS_PAGE, TASTE_OF_GOLD_DETAILS_PAGE, ESIM_DETAILS_PAGE, ESIM_INFO_PAGE, TIER_PROGRESSION_DETAILS_PAGE, TIER_UPGRADE_CAMPAIGN_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyPurchasePointsEnabled from 'loyalty/selectors/getIsLoyaltyPurchasePointsEnabled'
import LoyaltyInfiniteMemberRoute from 'loyalty/components/Routes/LoyaltyInfiniteMemberRoute'
import { isMaldivesCharterCarouselEnabled } from 'selectors/maldivesSelectors'
import CorporateEventRoutes from 'corporateEvents/CorporateEventRoutes'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { getAgentHubAffiliationRules } from 'agentHub/selectors/agentHubSelectors'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { isLEStaff } from 'selectors/accountSelectors'
import { CHARTER_BEFORE_YOU_FLY_PAGE_SLUG, CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG, FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM } from 'constants/friendsAndFamilyDiscount'
import RentalsPage from 'components/Pages/TypesPage/Rentals/RentalsPage'
import { MULTI_CLICK_UNSUB_SUPPORTED_BRANDS } from './constants'
import UrlLocation from 'src/lib/url/UrlLocation'
import { registerPageView } from 'actions/AnalyticsActions'
import AuthRouteComponent from './AuthRouteComponent'
import { isEnglishLocale } from 'i18n/util/locale'

const DestinationLandingPage = loadable(() => import('components/Pages/DestinationLandingPage/DestinationLandingPage'))
const CruiseLocationLandingPage = loadable(() => import('components/Cruises/CruiseSEOpage/CruiseLocationLandingPage'))
const CruiseShipsLandingPage = loadable(() => import('components/Cruises/CruiseSEOpage/CruiseShipsLandingPage'))
const HotelSearchFullScreenPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchFullScreenPage'))
const HotelSearchPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchPage'))
const MetaSearchPage = loadable(() => import('components/Pages/HotelSearchPage/MetaSearchPage'))
const HotelSearchMapPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchMapPage'))
const HotelSearchStoreMapPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchStoreMapPage'))
const SupportPage = loadable(() => import('components/Pages/SupportPage/SupportPage'))
const VideoChatPage = loadable(() => import('components/Pages/VideoChatPage'))
const AboutUsPage = loadable(() => import('components/Pages/AboutUsPage'))
const CareersPage = loadable(() => import('components/Pages/CareersPage'))
const StaticPage = loadable(() => import('components/Pages/StaticPage'))
const BestPricePage = loadable(() => import('components/Pages/BestPricePage'))
const FlightsLandingPage = loadable(() => import('components/Pages/PromotionalPages/FlightsLandingPage'))
const FlightsSearchLandingPage = loadable(() => import('components/Pages/PromotionalPages/FlightsLandingPage/FlightsSearchLandingPage'))
const FlightsSearchPage = loadable(() => import('components/Flights/FlightsSearchPage'))
const FlightsSearchResultsPage = loadable(() => import('components/Flights/FlightsSearchResultsPage'))
const FlightsSearchMultiCityResultsPage = loadable(() => import('components/Flights/FlightsSearchMultiCityResultsPage'))
const LegalPages = loadable(() => import('components/Pages/LegalPages'))
const ReferPage = loadable(() => import('marketing/pages/Refer'))
const VouchersPage = loadable(() => import('components/Pages/VouchersPage/VouchersPage'))
const NewsPage = loadable(() => import('components/Pages/NewsPage'))
const FrequentFlyerPage = loadable(() => import('components/Pages/FrequentFlyerPage'))
const MyEscapesPage = loadable(() => import('components/Pages/MyEscapesPage'))
const ShopOrdersPage = loadable(() => import('components/Pages/ShopOrdersPage'))
const UserReviews = loadable(() => import('components/Pages/UserReviews'))
const OrderPage = loadable(() => import('components/Pages/MyEscapesPage/OrderPage'))
const ShareOrderWebviewPage = loadable(() => import('components/Pages/MyEscapesPage/ShareOrderWebviewPage'))
const ShopOrderPage = loadable(() => import('components/Pages/ShopOrdersPage/OrderPage'))
const UpgradeRoomPage = loadable(() => import('components/Pages/MyEscapesPage/UpgradeRoomPage'))
const AirportTransferPage = loadable(() => import('components/Pages/MyEscapesPage/AirportTransfers'))
const PreferenceCentrePage = loadable(() => import('components/Pages/PreferenceCentrePage'))
const MySubscriptionsPage = loadable(() => import('components/Pages/MySubscriptionsPage'))
const MyWLSubscriptionsPage = loadable(() => import('components/Pages/MyWLSubscriptionsPage'))
const AccountDetailsPage = loadable(() => import('components/Pages/AccountDetailsPage'))
const AccountDetailsTravellerPage = loadable(() => import('components/Pages/AccountDetailsTravellerPage'))
const CreditsAndPaymentPage = loadable(() => import('components/Pages/CreditsPage/CreditsAndPaymentPage'))
const TourTravellerInfoPage = loadable(() => import('components/Pages/TourTravellerInfo/TourTravellerInfoPage'))
const GiftCardConfirmationPage = loadable(() => import('components/Pages/GiftCardsPage/Confirmation'))
const GiftCardViewPage = loadable(() => import('components/Pages/GiftCardsPage/View'))
const LandingPage = loadable(() => import('components/Pages/LandingPage/LandingPage'))
const TravelInsurancePage = loadable(() => import('components/Pages/Insurance/LandingPage'))
const TravelInsuranceCoverGeniusPage = loadable(() => import('components/Pages/Insurance/LandingPage/CoverGenius'))
const TravelInsuranceQuote = loadable(() => import('components/Pages/Insurance/QuotePage'))
const TravelInsuranceAddCheckout = loadable(() => import('components/Pages/Insurance/AddCheckoutPage'))
const WeddingPage = loadable(() => import('weddings/pages/WeddingPage'))
const WeddingSetupPage = loadable(() => import('weddings/pages/WeddingSetupPage'))
const WeddingGiftRegistryCartPage = loadable(() => import('weddings/pages/WeddingGiftRegistryCartPage'))
const WeddingsLandingPage = loadable(() => import('weddings/pages/WeddingsLandingPage'))
const LeLiveLandingPage = loadable(() => import('leLive/pages/LeLiveLandingPage'))
const TravelInsuranceDetails = loadable(() => import('components/Pages/Insurance/DetailsPage'))
const PassportCardDetailsPage = loadable(() => import('components/Pages/Insurance/PassportCardDetailsPage'))
const TravelInsurancePayment = loadable(() => import('components/Pages/Insurance/PaymentPage'))
const EscapesFinderPage = loadable(() => import('components/Pages/EscapesFinderPage'))
const ConfirmationPage = loadable(() => import('checkout/Components/Confirmation/ConfirmationPage'))
const BNBLLandingPage = loadable(() => import('components/Pages/BNBLLandingPage'))
const TWCLandingPage = loadable(() => import('components/Pages/TWCLandingPage'))
const EWCLandingPage = loadable(() => import('components/Pages/EWCLandingPage'))
const ContentHub = loadable(() => import('components/Pages/LandingPage/Hub'))
const OfferPage = loadable(() => import('components/OfferPage/OfferPage'))
const BedBankOfferPage = loadable(() => import('components/Pages/BedbankOfferPage'))
const PaginatedOffersPage = loadable(() => import('components/Pages/PaginatedOffersPage'))
const TourV2OfferPage = loadable(() => import('components/Pages/TourV2/OfferPage/TourV2OfferPage'))
const TourV2OfferVariationPage = loadable(() => import('components/Pages/TourV2/OfferPage/TourV2OfferVariationPage'))
const TourV2SearchPage = loadable(() => import('components/Pages/TourV2/SearchPage/TourV2SearchPage'))
const HotelsPage = loadable(() => import('components/Pages/TypesPage/Hotels/HotelsPage'))
const HomesAndVillasSearchPage = loadable(() => import('components/Pages/HomeAndVillas/Search/HomesAndVillasSearchPage'))
const HomesAndVillasSearchMapPage = loadable(() => import('components/Pages/HomeAndVillas/Search/HomesAndVillasSearchMapPage'))
const ToursPage = loadable(() => import('components/Pages/TypesPage/ToursPage'))
const UltraLuxVerticalPage = loadable(() => import('components/Pages/TypesPage/UltraLux/UltraLuxVerticalPage'))
const LuxBundlesPage = loadable(() => import('components/Pages/TypesPage/LuxBundlesPage'))
const DestinationProductPage = loadable(() => import('components/Pages/DestinationProductPage/DestinationProductPage'))
const GiftCardsPageContainer = loadable(() => import('components/Pages/GiftCardsPage'))
const PurchaseCreditsPage = loadable(() => import('components/Pages/PurchaseCreditsPage/PurchaseCreditsPage'))
const KlarnaLandingPage = loadable(() => import('components/Pages/PromotionalPages/KlarnaLandingPage'))
const AfterPayLandingPage = loadable(() => import('components/Pages/PromotionalPages/AfterPayLandingPage'))
const HoolahLandingPage = loadable(() => import('components/Pages/PromotionalPages/HoolahLandingPage'))
const RedirectToShopbackPage = loadable(() => import('components/Pages/PromotionalPages/RedirectToShopbackPage'))
const AtomeLandingPage = loadable(() => import('components/Pages/PromotionalPages/AtomeLandingPage'))
const WaaveLandingPage = loadable(() => import('components/Pages/PromotionalPages/WaaveLandingPage'))
const AccountPage = loadable(() => import('components/Pages/AccountPage/AccountLandingPage'))
const ExperienceOfferPage = loadable(() => import('components/Experiences/ExperienceOfferPage/ExperienceOfferPage'))
const ExperiencesPage = loadable(() => import('components/Experiences/ExperiencesPage/ExperiencesPage'))
const TransfersPage = loadable(() => import('components/Transfers/TransfersPage'))
const ExperienceSearchMapPage = loadable(() => import('components/Experiences/ExperiencesSearch/ExperienceSearchMapPage'))
const CruisesPage = loadable(() => import('components/Cruises/CruisesPage/CruisesPage'))
const CruiseSearchPage = loadable(() => import('components/Cruises/SearchPage/CruiseSearchPage'))
const CruiseOfferPage = loadable(() => import('components/Cruises/CruiseOfferPage/CruiseOfferPage'))
const CruiseLineLandingPage = loadable(() => import('components/Cruises/CruiseLineLandingPage/CruiseLineLandingPage'))
const CruiseDealPage = loadable(() => import('components/Cruises/CruiseDealPage/CruiseDealPage'))
const CruiseDealsListPage = loadable(() => import('components/Cruises/CruiseDealsListPage/CruiseDealsListPage'))
const ExperienceSearchPage = loadable(() => import('components/Experiences/ExperiencesSearch/ExperienceSearchPage'))
const TopPicksForMePage = loadable(() => import('components/Recommendations/TopPicksForMe/TopPicksForMePage'))
const RefereeLandingPage = loadable(() => import('../../../../content/pages/RefereeLanding'))
const GoogleHotelAdPage = loadable(() => import('../../../../marketing/pages/GoogleHotelAdPage'))
const CheckoutPageRoutes = loadable(() => import('checkout/routes'))
const CheckoutRestoreCartPage = loadable(() => import('checkout/Pages/RestoreCartPage'))
const CheckoutGatewayPage = loadable(() => import('checkout/Pages/GatewayPage'))
const RedirectCheckoutBNBL = loadable(() => import('checkout/Pages/RedirectCheckoutBNBL'))
const RedirectCheckoutChangeDates = loadable(() => import('checkout/Pages/RedirectCheckoutChangeDates'))
const RedirectCheckoutConfirmDates = loadable(() => import('checkout/Pages/RedirectCheckoutConfirmDates'))
const RedirectCheckoutExtendDates = loadable(() => import('checkout/Pages/RedirectCheckoutExtendDates'))
const RedirectCheckoutAddExtraGuests = loadable(() => import(/* webpackChunkName: "RedirectCheckoutAddExtraGuests" */ 'checkout/Pages/RedirectCheckoutAddExtraGuests'))
const RedirectCheckoutRoomUpgrade = loadable(() => import('checkout/Pages/RedirectCheckoutRoomUpgrade'))
const RedirectCheckoutFlightChangeDates = loadable(() => import('checkout/Pages/RedirectCheckoutFlightChangeDates'))
const RedirectCheckoutPartnerRoomUpgrade = loadable(() => import('checkout/Pages/RedirectCheckoutPartnerRoomUpgrade'))
const AncillaryPaymentPage = loadable(() => import('checkout/Pages/AncillaryPaymentPage'))
const AncillaryChangeReservationPage = loadable(() => import('checkout/Pages/AncillaryChangeReservationPage'))
const ExperienceVoucher = loadable(() => import('components/Pages/ExperienceVoucher'))
const PropertyPage = loadable(() => import('../../../../marketing/pages/PropertyPage'))
const RedeemGifted = loadable(() => import('components/Pages/RedeemGifted'))
const ExperienceGiftPrintPage = loadable(() => import('components/Pages/ExperienceGiftPrintPage'))
const ExperiencesListPage = loadable(() => import('components/Experiences/ExperiencesList/ExperiencesListPage'))
const AuCompetitionsPage = loadable(() => import('../../../../marketing/pages/AuCompetitionPage'))
const IntlCompetitionsPage = loadable(() => import('../../../../marketing/pages/INTLCompetitionPage'))
const ukCompetitionsPage = loadable(() => import('../../../../marketing/pages/UKCompetitionPage'))
const HolidayTypeLandingPage = loadable(() => import('components/Pages/HolidayTypeLandingPage'))
const CarHirePage = loadable(() => import('components/CarHire/CarHirePage/CarHirePage'))
const CarHireSearchPage = loadable(() => import('components/CarHire/CarHireSearchPage/CarHireSearchPage'))
const HomePage = loadable(() => import('../../../../home/pages/HomePage/HomePage'))
const ContentfulWebPage = loadable(() => import('../../../../marketing/pages/ContentfulWebPage/ContentfulWebPage'))
const LuxAwardPage = loadable(() => import('../../../../marketing/pages/ContentfulWebPage/LuxAward/LuxAwardPage'))
const LuxPlusLandingPage = loadable(() => import('luxPlus/pages/LuxPlusLandingPage'))
const LuxPlusAccountPage = loadable(() => import('luxPlus/pages/LuxPlusAccountPage'))
const LoyaltyLandingPage = loadable(() => import('loyalty/pages/LoyaltyLandingPageContainer'))
const InfiniteApplicationPage = loadable(() => import('loyalty/pages/infiniteOnboardingPage/InfiniteApplicationPage'))
const InfiniteOnboardingPage = loadable(() => import('loyalty/pages/infiniteOnboardingPage/InfiniteOnboardingPage'))
const LoyaltyOffersHubPage = loadable(() => import('loyalty/pages/loyaltyOffersHubPage/LoyaltyOffersHubPage'))
const LoungePassesPage = loadable(() => import('loyalty/pages/loungePassesPage/LoungePassesPage'))
const LoyaltyPurchasePointsPage = loadable(() => import('loyalty/pages/loyaltyPurchasePointsPage/LoyaltyPurchasePointsPage'))
const LoyaltyRewardsPage = loadable(() => import('loyalty/pages/loyaltyRewardsPage/LoyaltyRewardsPage'))
const LoyaltySpendPointsPage = loadable(() => import('loyalty/pages/loyaltySpendPointsPage/LoyaltySpendPointsPage'))
const ResetPasswordPage = loadable(() => import('components/Pages/ResetPasswordPage/ResetPasswordPage'))
const FlightDealLandingPage = loadable(() => import('components/Flights/FlightDealLandingPage/FlightDealLandingPage'))
const FlightCampaignsLandingPage = loadable(() => import('components/Flights/FlightCampaignsLandingPage/FlightCampaignsLandingPage'))
const TravelPreferencesPage = loadable(() => import('components/Pages/AccountTravelPreferencesPage/TravelPreferencesPage'))
const AgentHubHomePage = loadable(() => import('agentHub/pages/AgentHubHomepage'))
const AgentHubAccountSetupCompleted = loadable(() => import('agentHub/pages/AgentHubAccountSetup/AgentHubAccountSetupCompleted'))
const AgentHubToursPage = loadable(() => import('agentHub/pages/AgentHubToursPage'))
const SocieteConfirmationPage = loadable(() => import('agentHub/pages/SocieteConfirmationPage'))
const SkyCheckConfirmationPage = loadable(() => import('components/Pages/EmailConfirmedPage/SkyCheckConfirmation/SkyCheckConfirmationPage'))
const TailorMadeTourRequestPage = loadable(() => import('components/Pages/TailorMadeTour/TailorMadeTourRequestPage'))
const TailorMadeTourRequestConfirmationPage = loadable(() => import('components/Pages/TailorMadeTour/TailorMadeTourRequestConfirmationPage'))
const SecurePaymentPage = loadable(() => import('components/Pages/SecurePaymentPage'))
const WhitelabelHomePage = loadable(() => import('whitelabel/pages/WhitelabelHomePage'))
const TravelCompanionsPage = loadable(() => import('components/Pages/TravelCompanionsPage'))
const UserCartPage = loadable(() => import('components/Pages/UserCartPage/SharedCarts/MultiCartReviewWrapper'))
const SharedCartInvitePage = loadable(() => import('components/Pages/UserCartPage/SharedCarts/SharedCartInvitePage'))
const TourCataloguePage = loadable(() => import('components/Pages/TourV2/TourCataloguePage'))
const TourDealPage = loadable(() => import('components/Tours/Deals/TourDealPage'))
const WishlistsIndexPage = loadable(() => import('wishlists/pages/WishlistsIndexPage'))
const WishlistDetailPage = loadable(() => import('wishlists/pages/WishlistDetailPage'))
const MerchandiseOfferPage = loadable(() => import('components/Merchandise/MerchandiseOfferPage/MerchandiseOfferPage'))
const MerchandiseLandingPage = loadable(() => import('components/Merchandise/MerchandiseLandingPage/MerchandiseLandingPage'))
const MerchandiseSearchPage = loadable(() => import('components/Merchandise/MerchandiseSearchPage/MerchandiseSearchPage'))
const MerchandiseBrandsPage = loadable(() => import('components/Merchandise/MerchandiseBrandsPage/MerchandiseBrandsPage'))
const MerchandiseCategoryPage = loadable(() => import('components/Merchandise/MerchandiseCategoryPage/MerchandiseCategoryPage'))
const MerchandiseCategoriesPage = loadable(() => import('components/Merchandise/MerchandiseCategoriesPage'))
const TasteOfGoldDetailsPage = loadable(() => import('takeovers/tasteOfGoldTakeover/pages/TasteOfGoldDetailsPage'))
const LoyaltyEsimDetailsPage = loadable(() => import('loyalty/pages/LoyaltyEsimDetailsPage'))
const LoyaltyEsimInfoPage = loadable(() => import('loyalty/pages/LoyaltyEsimInfoPage'))
const TierProgressionDetailsPage = loadable(() => import('loyalty/pages/TierProgressionDetailsPage'))
const TierUpgradeCampaignPage = loadable(() => import('loyalty/pages/tierUpgradeCampaign/TierUpgradeCampaignPage'))
const TriplePointsTakeoverLandingPage = loadable(() => import('takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverLandingPage'))
const DoublePointsTakeoverLandingPage = loadable(() => import('takeovers/societeDoublePointsTakeover/components/DoublePointTakeoverLandingPage'))
const LuxPlusMonthTakeoverLandingPage = loadable(() => import('takeovers/luxPlusMonthTakeover/components/LuxPlusMonthTakeoverLandingPage'))
const SocieteNavBar = loadable(() => import('loyalty/components/SocieteNavBar'))
const MerchandiseNavBar = loadable(() => import('components/Merchandise/MerchandiseNavBar/MerchandiseNavBar'))
const ResubscribePage = loadable(() => import('components/Pages/ResubscribePage/ResubscribePage'))
const MultiClickUnsubPage = loadable(() => import('components/Pages/MultiClickUnsubPage/MultiClickUnsubPage'))
const MaldivesDirectSearchPage = loadable(() => import('components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectSearchPage'))
const MaldivesDirectBeforeYouFlyPage = loadable(() => import('components/MaldivesDirect/MaldivesDirectBeforeYouFlyPage/MaldivesDirectBeforeYouFlyPage'))
const FriendsAndFamilyInvitePage = loadable(() => import('components/FriendsAndFamilyDiscount/FriendsAndFamilyInvitePage/FriendsAndFamilyInvitePage'))
const AcceptOrderInvitePage = loadable(() => import('components/Pages/AcceptOrderInvitePage/AcceptOrderInvitePage'))

const promotionalPages: Record<string, LoadableComponent<any>> = {
  GiftCardsPageContainer,
  KlarnaLandingPage,
  AfterPayLandingPage,
  HoolahLandingPage,
  RedirectToShopbackPage,
  AtomeLandingPage,
  WaaveLandingPage,
}

interface Props {
  currentRegionCode: string
  standaloneFlightsEnabled: boolean
  standaloneCruiseEnabled: boolean
  isLuxPlusEnabled: boolean
  isLoyaltyEnabled: boolean
}

function DefaultHomePageRedirect() {
  const location = useLocation()

  return <RedirectWithStatus
    code={302}
    to={location.pathname.replace(/$/, config.defaultHomePage)}
  />
}

function pageElement(Component: ComponentType<any>) {
  return createElement(Component)
}

function RentalsRouteElement() {
  const { regionCode } = useParams()

  if (regionCode !== 'au') {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return <RentalsPage />
}

function MultiClickUnsubRouteElement() {
  if (!MULTI_CLICK_UNSUB_SUPPORTED_BRANDS.includes(config.BRAND)) {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return <MultiClickUnsubPage />
}

function ShareThisVerifyRouteElement(): null {
  const mainUrl = `https://platform-api.sharethis.com/js/sharethis.js#property=${config.GOOGLE_APP_ID}`
  const script = document.createElement('script')
  script.setAttribute('id', 'sharethis-js')
  script.src = mainUrl
  script.async = true
  document.body.appendChild(script)
  return null
}

function OfferPageElement() {
  const { offerId } = useParams()

  return <OfferPage key={offerId} />
}

function ExperienceOfferPageElement() {
  const { id, offerId } = useParams()

  return <ExperienceOfferPage key={offerId ?? id} />
}

function BedBankOfferPageElement() {
  const { offerId } = useParams()

  return <BedBankOfferPage key={offerId} />
}

function TourV2OfferPageElement() {
  const { offerId } = useParams()

  return <TourV2OfferPage key={offerId} />
}

function FriendsReferralRedirectElement() {
  const { regionCode, referralEmail } = useParams()
  const clientRedirectTo = useMemo(() => {
    if (!regionCode || !referralEmail) return undefined

    const searchParams = new URLSearchParams({
      [FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM]: referralEmail,
    })

    return `/${regionCode}/campaign/${CHARTER_PAGE_SLUG}?${searchParams.toString()}`
  }, [referralEmail, regionCode])

  useIsomorphicLayoutEffect(() => {
    if (!clientRedirectTo) return

    window.location.replace(clientRedirectTo)
  }, [clientRedirectTo])

  if (typeof window !== 'undefined' && clientRedirectTo) {
    return null
  }

  return (
    <RedirectWithStatus
      code={302}
      exact
      from="/:regionCode/friends/:referralEmail"
      to={`/:regionCode/campaign/${CHARTER_PAGE_SLUG}?${FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM}=:referralEmail`}
    />
  )
}

function HolidayLandingPageElement() {
  return <LandingPage typeOfPage={TYPE_OF_PAGE_HOLIDAY} />
}

function DestinationProductDotRouteElement() {
  const location = useLocation()
  const match = matchDestinationProductDotRoute(new UrlLocation(location.pathname).withoutLocale().path)

  if (!match) {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return createElement(DestinationProductPage, { match })
}

function FriendsAndFamilyInviteRouteElement(props: { isEnabled: boolean, isStaff: boolean }) {
  if (!props.isStaff || !props.isEnabled) {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return <FriendsAndFamilyInvitePage />
}

function StaticPageRouteElement(props: { staticPage: typeof config.staticPages[number], title: string }) {
  const location = useLocation()
  const { staticPage, title } = props

  if (staticPage.type === 'bestPrice') {
    return <BestPricePage />
  } else if (staticPage.type === 'aboutUs') {
    return <AboutUsPage />
  } else if (staticPage.type === 'careersAndCulture') {
    return <CareersPage />
  } else if (staticPage.type === 'termsAndConditions') {
    return <LegalPages slug="terms-and-conditions" location={location} />
  } else if (staticPage.type === 'luxPlusTermsAndConditions') {
    return <LegalPages slug={LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE} location={location} />
  } else if (staticPage.type === 'societeTermsAndConditions') {
    return <LegalPages slug="societe-terms-and-conditions" location={location} />
  } else if (staticPage.type === 'societeFAQs') {
    return <LegalPages slug="societe-frequently-asked-questions" location={location} />
  } else if (staticPage.type === 'shopTermsAndConditions') {
    return <LegalPages slug="shop-terms-and-conditions" location={location} />
  } else if (staticPage.type === 'shopFAQs') {
    return <LegalPages slug="shop-frequently-asked-questions" location={location} />
  } else if (staticPage.type === 'refundPolicy') {
    return <LegalPages slug="refund-policy" location={location} />
  } else if (staticPage.type === 'news') {
    return <NewsPage slug="news" location={location} />
  } else if (staticPage.type === 'privacyPolicy') {
    return <LegalPages slug="privacy-policy" location={location} />
  } else if (staticPage.type === 'giftcardTermsAndConditions') {
    return <LegalPages slug="giftcards-terms" location={location} />
  } else if (staticPage.type === 'flights') {
    return <LegalPages slug="flights-policies" location={location} />
  } else if (staticPage.type === 'frequentFlyerPrograms') {
    return <FrequentFlyerPage slug="frequent-flyer-programs" location={location} />
  } else if (staticPage.type === 'specialProductsPromotions') {
    return <LegalPages slug="promotions" location={location} />
  } else if (staticPage.type === 'bookingPolicies') {
    return <LegalPages slug="booking-policies" location={location} />
  } else if (staticPage.type === 'bestPriceTerms') {
    return <LegalPages slug="best-price-guarantee-terms" location={location} />
  } else if (staticPage.type === 'feeSchedule') {
    return <LegalPages slug="fee-schedule" location={location} />
  } else if (staticPage.type === 'travelInsurance') {
    return <TravelInsurancePage />
  } else if (staticPage.type === 'travelInsuranceCoverGenius') {
    return <TravelInsuranceCoverGeniusPage />
  } else if (staticPage.type === 'bnblLanding') {
    return <BNBLLandingPage />
  } else if (staticPage.type === 'twcLanding') {
    return <TWCLandingPage />
  } else if (staticPage.type === 'ewcLanding') {
    return <EWCLandingPage />
  } else if (staticPage.type === 'affiliatePage') {
    return <ContentfulWebPage />
  }

  return <StaticPage type={staticPage.type} title={title} />
}

interface NativeTrackedRoute {
  element: ReactNode
  index?: boolean
  path?: string
  trackingPath?: string
}

interface LocaleAwareTrackedRoutesProps {
  routes: Array<NativeTrackedRoute>
}

interface AuthenticatedRouteElementProps {
  accountAccessModalInitialMode?: App.UiAccountModalMode
  children?: ReactNode
  loader?: ReactNode
  showPopup?: boolean
  path?: string
}

interface TrackedRouteElementProps {
  element?: ReactNode
  path: string
}

function LocaleAwareTrackedRoutes(props: LocaleAwareTrackedRoutesProps) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => {
    return {
      ...location,
      pathname: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location])
  const matchedRoute = useMemo(() => {
    const trackingRoutes = props.routes
      .map(route => ({ element: route.element, path: route.trackingPath ?? route.path }))
      .filter((route): route is { element: ReactNode; path: string } => route.path !== undefined)

    return matchRoutes(trackingRoutes, localeStrippedLocation)?.at(-1)
  }, [localeStrippedLocation, props.routes])

  useLayoutEffect(() => {
    if (!matchedRoute) return

    dispatch(registerPageView({
      template: matchedRoute.route.path,
      params: matchedRoute.params,
      path: matchedRoute.pathname,
      search: location.search,
    }))
  }, [dispatch, location.search, matchedRoute])

  return (
    <RouterRoutes location={localeStrippedLocation}>
      {props.routes.map(route => {
        if (route.index) {
          return (
            <RouterRoute
              key={route.trackingPath ?? 'index'}
              index
              element={route.element}
            />
          )
        }

        return (
          <RouterRoute
            key={route.trackingPath ?? route.path}
            path={route.path}
            element={route.element}
          />
        )
      })}
    </RouterRoutes>
  )
}

function TrackedRouteElement(props: TrackedRouteElementProps) {
  const location = useLocation()
  const params = useParams()
  const match = useMemo<AppRouteMatch>(() => ({
    isExact: true,
    params,
    path: props.path,
    url: new UrlLocation(location.pathname).withoutLocale().path,
  }), [location.pathname, params, props.path])

  useRegisterPageView(location, match)

  if (props.element) {
    if (isValidElement(props.element)) {
      return cloneElement(props.element as ReactElement<any>, { location, match })
    }

    return <>{props.element}</>
  }

  return null
}

function AuthenticatedRouteElement(props: AuthenticatedRouteElementProps) {
  const {
    accountAccessModalInitialMode,
    children,
    loader,
    path,
    showPopup,
  } = props
  const location = useLocation()
  const params = useParams()
  const match = useMemo<AppRouteMatch | null>(() => {
    if (!path) return null

    return {
      isExact: true,
      params,
      path,
      url: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location.pathname, params, path])

  return (
    <AuthRouteComponent
      accountAccessModalInitialMode={accountAccessModalInitialMode}
      loader={loader}
      match={match}
      showPopup={showPopup}
    >

      {children}
    </AuthRouteComponent>)
}

function toPageRoutePath(path: string, end?: boolean): string {
  if (end || path === '*' || path.endsWith('*')) {
    return path
  }

  return path.endsWith('/') ? `${path}*` : `${path}/*`
}

function toRelativeChildRoutePath(parentPath: string, childPath: string): string | undefined {
  if (childPath === parentPath) {
    return undefined
  }

  const parentPrefix = `${parentPath}/`
  return childPath.startsWith(parentPrefix) ? childPath.slice(parentPrefix.length) : childPath
}

function toRegionalRoutePath(path: string): string {
  return `/:regionCode/${path}`
}

function trackedChildRoute(parentPath: string, childPath: string, element: ReactNode): NativeTrackedRoute {
  const relativePath = toRelativeChildRoutePath(parentPath, childPath)

  if (!relativePath) {
    return {
      element,
      index: true,
      trackingPath: toRegionalRoutePath(childPath),
    }
  }

  return {
    element,
    path: relativePath,
    trackingPath: toRegionalRoutePath(childPath),
  }
}

function useRegisterPageView(location: ReturnType<typeof useLocation>, match: AppRouteMatch | null) {
  const dispatch = useAppDispatch()

  useLayoutEffect(() => {
    if (!match) return

    dispatch(registerPageView({
      template: match.path,
      params: match.params,
      path: match.url,
      search: location.search,
    }))
  }, [dispatch, location.search, match])
}

function PageRoutes(props: Props) {
  const intl = useIntl()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => ({
    ...location,
    pathname: new UrlLocation(location.pathname).withoutLocale().path,
  }), [location])
  const {
    currentRegionCode,
    standaloneFlightsEnabled,
    standaloneCruiseEnabled,
    isLuxPlusEnabled,
    isLoyaltyEnabled,
  } = props

  const hasSetupAgentHubAccount = useAppSelector((state) => state.agentHub.agentHubAccount.account?.status === 'onboarded')
  const agentHubAffiliationRules = useAppSelector((state) => getAgentHubAffiliationRules(state))
  const isShopEnabled = useAppSelector(getIsMerchandiseEnabled)
  const locale = useAppSelector((state) => state.geo.locale)
  const isShopLocale = isEnglishLocale(locale)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const isStaff = useAppSelector(isLEStaff)
  const isFriendsAndFamilyPageEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterFnfPage)

  const canBookCruise = !isAgentHubEnabled || agentHubAffiliationRules.canBookCruise
  const canBookCarHire = !isAgentHubEnabled || agentHubAffiliationRules.canBookCarHire
  const isLoyaltyLuxplusLandingPageEnabled = useFeatureFlag(OptimizelyFeatureFlags.loyaltyLuxplusLandingPage)
  const isLoyaltyPurchasePointsEnabled = useAppSelector(getIsLoyaltyPurchasePointsEnabled)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterCarouselEnabled)
  const loyaltyRoutes = useMemo<Array<NativeTrackedRoute>>(() => {
    const routes: Array<NativeTrackedRoute> = [
      trackedChildRoute(LOYALTY_LANDING_PAGE, TASTE_OF_GOLD_DETAILS_PAGE, <TasteOfGoldDetailsPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, ESIM_DETAILS_PAGE, <LoyaltyEsimDetailsPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, ESIM_INFO_PAGE, <LoyaltyEsimInfoPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, TIER_PROGRESSION_DETAILS_PAGE, <TierProgressionDetailsPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, LOUNGE_PASSES_PAGE, <LoungePassesPage />)]

    if (isLoyaltyPurchasePointsEnabled) {
      routes.push(trackedChildRoute(LOYALTY_LANDING_PAGE, LOYALTY_PURCHASE_POINTS_PAGE, <LoyaltyPurchasePointsPage />))
    }

    if (isMaldivesCharterEnabled) {
      routes.push(trackedChildRoute(LOYALTY_LANDING_PAGE, LOYALTY_REWARDS_PAGE, <LoyaltyRewardsPage />))
    }

    routes.push(
      trackedChildRoute(LOYALTY_LANDING_PAGE, LOYALTY_SPEND_POINTS_PAGE, <LoyaltySpendPointsPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, LOYALTY_OFFERS_HUB_PAGE, <LoyaltyOffersHubPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, TIER_UPGRADE_CAMPAIGN_PAGE, <TierUpgradeCampaignPage />),
      trackedChildRoute(LOYALTY_LANDING_PAGE, LOYALTY_LANDING_PAGE, <LoyaltyLandingPage />),
    )

    return routes
  }, [isLoyaltyPurchasePointsEnabled, isMaldivesCharterEnabled])
  const merchandiseShopNavRoutes = useMemo<Array<NativeTrackedRoute>>(() => [
    { index: true, trackingPath: '/:regionCode/shop', element: <MerchandiseNavBar /> },
    { path: 'category/:categorySlug', trackingPath: '/:regionCode/shop/category/:categorySlug', element: <MerchandiseNavBar /> }],
  [])
  const merchandiseShopPageRoutes = useMemo<Array<NativeTrackedRoute>>(() => [
    { index: true, trackingPath: '/:regionCode/shop', element: <MerchandiseLandingPage /> },
    { path: 'offer/:offerId', trackingPath: '/:regionCode/shop/offer/:offerId', element: <MerchandiseOfferPage /> },
    { path: 'offer/:slug/:offerId', trackingPath: '/:regionCode/shop/offer/:slug/:offerId', element: <MerchandiseOfferPage /> },
    { path: 'brands', trackingPath: '/:regionCode/shop/brands', element: <MerchandiseBrandsPage /> },
    { path: 'categories', trackingPath: '/:regionCode/shop/categories', element: <MerchandiseCategoriesPage /> },
    { path: 'category/:categorySlug', trackingPath: '/:regionCode/shop/category/:categorySlug', element: <MerchandiseCategoryPage /> }],
  [])
  const merchandiseSearchNavRoutes = useMemo<Array<NativeTrackedRoute>>(() => [
    { path: 'brands/:brandSlug/:brandId', trackingPath: '/:regionCode/search/shop/brands/:brandSlug/:brandId', element: <MerchandiseNavBar /> },
    { path: ':parentCategorySlug/:categorySlug', trackingPath: '/:regionCode/search/shop/:parentCategorySlug/:categorySlug', element: <MerchandiseNavBar /> },
    { path: ':categorySlug?', trackingPath: '/:regionCode/search/shop/:categorySlug?', element: <MerchandiseNavBar /> }],
  [])
  const merchandiseSearchPageRoutes = useMemo<Array<NativeTrackedRoute>>(() => [
    { path: 'brands/:brandSlug/:brandId', trackingPath: '/:regionCode/search/shop/brands/:brandSlug/:brandId', element: <MerchandiseSearchPage /> },
    { path: ':parentCategorySlug/:categorySlug', trackingPath: '/:regionCode/search/shop/:parentCategorySlug/:categorySlug', element: <MerchandiseSearchPage /> },
    { path: ':categorySlug?', trackingPath: '/:regionCode/search/shop/:categorySlug?', element: <MerchandiseSearchPage /> }],
  [])
  const friendsReferralRoutes = useMemo<Array<NativeTrackedRoute>>(() => [
    { path: ':referralEmail', trackingPath: '/:regionCode/friends/:referralEmail', element: <FriendsReferralRedirectElement /> }],
  [])
  const isLoyaltyRoute = useMemo(() => !!matchLocaleAwarePath(location.pathname, `/:regionCode/${LOYALTY_LANDING_PAGE}`), [location.pathname])
  const isTripPlannerRoute = useMemo(() => !!matchLocaleAwarePath(location.pathname, '/:regionCode/trip-planner'), [location.pathname])
  const isMerchandiseRoute = useMemo(() => !!matchLocaleAwarePath(location.pathname, [
    '/:regionCode/shop',
    '/:regionCode/search/shop'],
  ), [location.pathname])
  const corporateEventRouteMatch = useMemo(() => {
    return matchLocaleAwarePath<{ eventId: string; regionCode: string; }>(location.pathname, '/:regionCode/corporate-events/:eventId')
  }, [location.pathname])

  if (config.agentHub.isEnabled && !hasSetupAgentHubAccount) {
    return <AgentHubGuestPageRoutes />
  }

  return (
    <RouterRoutes location={localeStrippedLocation}>
      {/* Tour v1 redirects */}
      {Object.keys(config.toursV1Redirects).map((tourV1) => <RouterRoute

        key={tourV1}
        path={toPageRoutePath(

          `/:regionCode/offer/${tourV1}`, false)}
        element={<RedirectWithStatus
          key={tourV1}
          code={301}
          from={`/:regionCode/offer/${tourV1}`}
          to={`/tour/${config.toursV1Redirects[tourV1]}`}
          withSearchAndHash={false}
        />}
      />,

      )}
      {/* Tour v2 redirects */}
      {Object.keys(config.toursV2Redirects).flatMap((tourV2) => [<RouterRoute

        key={`${tourV2}-variation`}
        path={toPageRoutePath(

          `/:regionCode/tour/${tourV2}/:variationId`, false)}
        element={<RedirectWithStatus
          key={`${tourV2}-variation`}
          code={301}
          from={`/:regionCode/tour/${tourV2}/:variationId`}
          to={`/tour/${config.toursV2Redirects[tourV2]}`}
        />}
      />, <RouterRoute

        key={tourV2}
        path={toPageRoutePath(

          `/:regionCode/tour/${tourV2}`, false)}
        element={<RedirectWithStatus
          key={tourV2}
          code={301}
          from={`/:regionCode/tour/${tourV2}`}
          to={`/tour/${config.toursV2Redirects[tourV2]}`}
        />}
      />],

      )}
      {/* Offer list Pages */}
      {config.agentHub.isEnabled && <RouterRoute path={toPageRoutePath(`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`, true)} element={<TrackedRouteElement element={<AgentHubToursPage />} path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} />} />}
      <RouterRoute path={toPageRoutePath(`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`, true)} element={<TrackedRouteElement element={<ToursPage />} path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} />} />
      <RouterRoute path={toPageRoutePath(`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}`, true)} element={<TrackedRouteElement element={<HotelsPage />} path={`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}`} />} />
      <RouterRoute
        path={toPageRoutePath('/:regionCode/rentals', true)}
        element={<TrackedRouteElement
          element={

            <RentalsRouteElement />
          }
          path="/:regionCode/rentals"
        />}
      />

      <RouterRoute path={toPageRoutePath(`/:regionCode/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`, true)} element={<TrackedRouteElement element={<LuxBundlesPage />} path={`/:regionCode/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`} />} />
      {config.ULTRALUX_ENABLED && <RouterRoute path={toPageRoutePath(`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}`, true)} element={<TrackedRouteElement element={<UltraLuxVerticalPage />} path={`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}`} />} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/experiences', true)} element={<TrackedRouteElement element={<ExperiencesPage />} path="/:regionCode/experiences" />} />}
      {config.EXPERIENCE_TRANSFERS_VERTICAL_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/transfers', true)} element={<TrackedRouteElement element={<TransfersPage />} path="/:regionCode/transfers" />} />}
      {/* Cruises */}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises', true)} element={<TrackedRouteElement element={<CruisesPage />} path="/:regionCode/cruises" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/deals', true)} element={<TrackedRouteElement element={<CruiseDealsListPage />} path="/:regionCode/cruises/deals" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/departures/:departureLocation', false)} element={<TrackedRouteElement element={<CruiseLocationLandingPage />} path="/:regionCode/cruises/departures/:departureLocation" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/destinations/:destinationLocation', false)} element={<TrackedRouteElement element={<CruiseLocationLandingPage />} path="/:regionCode/cruises/destinations/:destinationLocation" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/ships/:shipNames', false)} element={<TrackedRouteElement element={<CruiseShipsLandingPage />} path="/:regionCode/cruises/ships/:shipNames" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/deal/:slug', true)} element={<TrackedRouteElement element={<CruiseDealPage />} path="/:regionCode/cruises/deal/:slug" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/:offerId', true)} element={<TrackedRouteElement element={<CruiseOfferPage />} path="/:regionCode/cruises/:offerId" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises/:offerSlug/:offerId', true)} element={<TrackedRouteElement element={<CruiseOfferPage />} path="/:regionCode/cruises/:offerSlug/:offerId" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruise-line/:cruiseLineSlug', true)} element={<TrackedRouteElement element={<CruiseLineLandingPage />} path="/:regionCode/cruise-line/:cruiseLineSlug" />} />}
      {!standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruises', false)} element={<RedirectWithStatus code={302} from="/:regionCode/cruises" to="/:regionCode/tours" />} />}
      {!standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/search/cruises', false)} element={<RedirectWithStatus code={302} from="/:regionCode/search/cruises" to="/:regionCode/tours" />} />}
      {!standaloneCruiseEnabled && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/cruise-line', false)} element={<RedirectWithStatus code={302} from="/:regionCode/cruise-line" to="/:regionCode/tours" />} />}
      {/* Car Hire Pages */}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && canBookCarHire && <RouterRoute path={toPageRoutePath('/:regionCode/car-hire', true)} element={<TrackedRouteElement element={<CarHirePage />} path="/:regionCode/car-hire" />} />}
      {/* Offer pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/offer/:offerId', true)} element={<TrackedRouteElement element={<OfferPageElement />} path="/:regionCode/offer/:offerId" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/offer/:offerSlug/:offerId', false)} element={<TrackedRouteElement element={<OfferPageElement />} path="/:regionCode/offer/:offerSlug/:offerId" />} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/experience/:id', true)} element={<TrackedRouteElement element={<ExperienceOfferPageElement />} path="/:regionCode/experience/:id" />} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/experience/:slug/:id', false)} element={<TrackedRouteElement element={<ExperienceOfferPageElement />} path="/:regionCode/experience/:slug/:id" />} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/experiences/:locationSlug/:categorySlug', true)} element={<TrackedRouteElement element={<ExperiencesListPage />} path="/:regionCode/experiences/:locationSlug/:categorySlug" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/experiences/:locationSlug', true)} element={<TrackedRouteElement element={<ExperiencesListPage />} path="/:regionCode/experiences/:locationSlug" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/partner/:offerSlug/:offerId', false)} element={<TrackedRouteElement element={<BedBankOfferPageElement />} path="/:regionCode/partner/:offerSlug/:offerId" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/page/:pageNum', false)} element={<TrackedRouteElement element={<PaginatedOffersPage />} path="/:regionCode/page/:pageNum" />} />
      {/* If brand defines a particular default homepage, redirect "/" to that location */}
      {
        config.defaultHomePage && <RouterRoute
          path={toPageRoutePath('/:regionCode/', true)}
          element={<TrackedRouteElement
            element={

              <DefaultHomePageRedirect />
            }
            path="/:regionCode/"
          />}
        />

      }
      {config.agentHub.isEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/', true)} element={<TrackedRouteElement element={<AgentHubHomePage />} path="/:regionCode/" />} />}
      {config.WHITELABEL_HOME_PAGE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/', true)} element={<TrackedRouteElement element={<WhitelabelHomePage />} path="/:regionCode/" />} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/', true)} element={<TrackedRouteElement element={<HomePage />} path="/:regionCode/" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/villas', false)} element={<RedirectWithStatus code={301} from="/:regionCode/villas" to="/:regionCode/holidays/homes-and-villas" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/doublesix', false)} element={<RedirectWithStatus code={301} from="/:regionCode/doublesix" to="/:regionCode/offer/double-six-luxury-hotel-seminyak-bali-indonesia-asia/006RF00000KMiBlYAL" />} />
      {/* Support Pages */}
      {config.SUPPORT_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/support', false)} element={<TrackedRouteElement element={<SupportPage />} path="/:regionCode/support" />} />}
      {/* Corporate Events */}
      {config.CORPORATE_EVENTS_ENABLED && corporateEventRouteMatch && <RouterRoute
        path="/:regionCode/corporate-events/:eventId/*"
        element={
          <CorporateEventRoutes
            eventId={corporateEventRouteMatch.params.eventId}
            regionCode={corporateEventRouteMatch.params.regionCode}
          />
        }
      />}
      {/* Weddings */}
      {config.WEDDINGS_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/weddings/create', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/weddings/create"><WeddingSetupPage /></AuthenticatedRouteElement>} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/weddings/:slug/edit', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/weddings/:slug/edit"><WeddingSetupPage /></AuthenticatedRouteElement>} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path={toPageRoutePath(WEDDING_GIFT_REGISTRY_CART_ROUTE, true)} element={<AuthenticatedRouteElement showPopup path={WEDDING_GIFT_REGISTRY_CART_ROUTE}><WeddingGiftRegistryCartPage /></AuthenticatedRouteElement>} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/weddings/:slug', true)} element={<TrackedRouteElement element={<WeddingPage />} path="/:regionCode/weddings/:slug" />} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/weddings', true)} element={<TrackedRouteElement element={<WeddingsLandingPage />} path="/:regionCode/weddings" />} />}
      {/* LE Live */}
      {config.LE_LIVE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/live', true)} element={<TrackedRouteElement element={<LeLiveLandingPage />} path="/:regionCode/live" />} />}
      {/* Sales Team Contact Page */}
      <RouterRoute path={toPageRoutePath('/:regionCode/request-video-chat/:offerId/:packageId', false)} element={<TrackedRouteElement element={pageElement(VideoChatPage)} path="/:regionCode/request-video-chat/:offerId/:packageId" />} />
      {/* LuxPlus */}
      {isLuxPlusEnabled && <RouterRoute
        path={toPageRoutePath(
        `/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}/:tab?`, false)}
        element={<TrackedRouteElement element={<LuxPlusLandingPage />} path={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}/:tab?`} />}
      />}
      {isLuxPlusEnabled && <RouterRoute
        path={toPageRoutePath('/:regionCode/luxplus', false)}
        element={
          <RedirectWithStatus code={301} from="/:regionCode/luxplus" to={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} />
        }
      />}

      {/* Loyalty */}
      {isLoyaltyEnabled && <RouterRoute
        path={toPageRoutePath(`/:regionCode/${LOYALTY_INFINITE_ONBOARDING_PAGE}`, false)}
        element={<LoyaltyInfiniteMemberRoute showPopup path={`/:regionCode/${LOYALTY_INFINITE_ONBOARDING_PAGE}`} element={<InfiniteOnboardingPage />} />}
      />}
      {isLoyaltyEnabled && <RouterRoute
        path={toPageRoutePath(`/:regionCode/${LOYALTY_INFINITE_REGISTER_INTEREST_PAGE}`, false)}
        element={<TrackedRouteElement element={<InfiniteApplicationPage />} path={`/:regionCode/${LOYALTY_INFINITE_REGISTER_INTEREST_PAGE}`} />}
      />}
      {isLoyaltyEnabled && isLoyaltyRoute &&
        <RouterRoute
          path={`/:regionCode/${LOYALTY_LANDING_PAGE}/*`}
          element={
            <>
              <SocieteNavBar />
              <LocaleAwareTrackedRoutes routes={loyaltyRoutes} />
            </>
          }
        />}
      {isLoyaltyEnabled && <RouterRoute
        path={toPageRoutePath('/:regionCode/society', false)}
        element={
          <RedirectWithStatus code={301} from="/:regionCode/society" to={`/:regionCode/${LOYALTY_LANDING_PAGE}`} />
        }
      />}
      {isLoyaltyEnabled && <RouterRoute
        path={toPageRoutePath('/:regionCode/société', false)}
        element={
          <RedirectWithStatus code={301} from="/:regionCode/société" to={`/:regionCode/${LOYALTY_LANDING_PAGE}`} />
        }
      />}
      {isLoyaltyEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/triple-points', false)} element={<TrackedRouteElement element={<TriplePointsTakeoverLandingPage />} path="/:regionCode/triple-points" />} />}
      {isLoyaltyEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/double-status-credits', false)} element={<TrackedRouteElement element={<DoublePointsTakeoverLandingPage />} path="/:regionCode/double-status-credits" />} />}
      {isLuxPlusEnabled && <RouterRoute path={toPageRoutePath(`/:regionCode${LUX_PLUS_MONTH_LANDING_PAGE_URL}`, false)} element={<TrackedRouteElement element={<LuxPlusMonthTakeoverLandingPage />} path={`/:regionCode${LUX_PLUS_MONTH_LANDING_PAGE_URL}`} />} />}
      {/* Search Pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/search-fullscreen', false)} element={<TrackedRouteElement element={<HotelSearchFullScreenPage />} path="/:regionCode/search-fullscreen" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/search', true)} element={<TrackedRouteElement element={<HotelSearchPage />} path="/:regionCode/search" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/metasearch/:propertyId', true)} element={<TrackedRouteElement element={<MetaSearchPage />} path="/:regionCode/metasearch/:propertyId" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/search/map', true)} element={<TrackedRouteElement element={pageElement(HotelSearchMapPage)} path="/:regionCode/search/map" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/search/store/map', true)} element={<TrackedRouteElement element={pageElement(HotelSearchStoreMapPage)} path="/:regionCode/search/store/map" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/search/homes-and-villas', true)} element={<TrackedRouteElement element={<HomesAndVillasSearchPage />} path="/:regionCode/search/homes-and-villas" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/search/homes-and-villas/map', true)} element={<TrackedRouteElement element={pageElement(HomesAndVillasSearchMapPage)} path="/:regionCode/search/homes-and-villas/map" />} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/search/experiences', true)} element={<TrackedRouteElement element={<ExperienceSearchPage />} path="/:regionCode/search/experiences" />} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path={toPageRoutePath('/:regionCode/search/experiences/map', true)} element={<TrackedRouteElement element={<ExperienceSearchMapPage />} path="/:regionCode/search/experiences/map" />} />}
      {config.CRUISES_STANDLONE_ENABLED && canBookCruise && <RouterRoute path={toPageRoutePath('/:regionCode/search/cruises', true)} element={<TrackedRouteElement element={<CruiseSearchPage />} path="/:regionCode/search/cruises" />} />}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && canBookCarHire && <RouterRoute path={toPageRoutePath('/:regionCode/search/car-hire', true)} element={<TrackedRouteElement element={<CarHireSearchPage />} path="/:regionCode/search/car-hire" />} />}
      <RouterRoute path={toPageRoutePath('/velocity-frequent-flyer', true)} element={<RedirectWithStatus code={301} exact from="/velocity-frequent-flyer" to="/" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/velocity-frequent-flyer', false)} element={<RedirectWithStatus code={301} from="/:regionCode/velocity-frequent-flyer" to="/:regionCode/" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/chadstone.p1', false)} element={<RedirectWithStatus code={301} from="/:regionCode/chadstone.p1" to="/:regionCode/campaign/store" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/amex', false)} element={<RedirectWithStatus code={301} from="/:regionCode/amex" to="/:regionCode/" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/refund-policy', false)} element={<RedirectWithStatus code={301} from="/:regionCode/refund-policy" to="/:regionCode/booking-policies" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/frequently-asked-questions', false)} element={<RedirectWithStatus code={301} from="/:regionCode/frequently-asked-questions" to="/:regionCode/support" />} />
      {/* Account pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-destinations', false)} element={<RedirectWithStatus code={301} from="/:regionCode/account/my-destinations" to="/:regionCode/account" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-escapes/upgrade-room/:orderId', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/upgrade-room/:orderId"><UpgradeRoomPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account', true)} element={isAgentHubEnabled ? <RedirectWithStatus code={302} exact from="/:regionCode/account" to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account"><AccountPage /></AuthenticatedRouteElement>} />
      {config.ENABLE_TRIP_PLANNER && <RouterRoute path={toPageRoutePath('/:regionCode/wishlists', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/wishlists"><WishlistsIndexPage /></AuthenticatedRouteElement>} />}
      {config.ENABLE_TRIP_PLANNER && <RouterRoute path={toPageRoutePath('/:regionCode/wishlists/:wishlistId', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/wishlists/:wishlistId"><WishlistDetailPage /></AuthenticatedRouteElement>} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/account/credits-payments', true)} element={isAgentHubEnabled ? <RedirectWithStatus code={302} exact from="/:regionCode/account/credits-payments" to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account/credits-payments"><CreditsAndPaymentPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/vouchers', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/vouchers"><VouchersPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher"><ExperienceVoucher /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-escapes/order/:orderId', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/order/:orderId"><OrderPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/booking-authority/share', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/booking-authority/share"><ShareOrderWebviewPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/order/:orderId/experience-gift-details', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/order/:orderId/experience-gift-details"><ExperienceGiftPrintPage /></AuthenticatedRouteElement>} />
      {isShopEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/account/shop-orders/order/:orderId', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/shop-orders/order/:orderId"><ShopOrderPage /></AuthenticatedRouteElement>} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/account/travel-preferences', true)} element={<AuthenticatedRouteElement path="/:regionCode/account/travel-preferences"><TravelPreferencesPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/travel-companions', true)} element={isAgentHubEnabled ? <RedirectWithStatus code={302} exact from="/:regionCode/account/travel-companions" to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account/travel-companions"><TravelCompanionsPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-escapes/order/:orderId/airport-transfers/:experienceItemId', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/order/:orderId/airport-transfers/:experienceItemId"><AirportTransferPage /></AuthenticatedRouteElement>} />
      {/* Apps are still pointing to /my-escapes, so redirect to the updated route */}
      <RouterRoute path={toPageRoutePath('/:regionCode/my-escapes/:modalType?', false)} element={<RedirectWithStatus code={301} from="/:regionCode/my-escapes/:modalType?" to="/:regionCode/account/my-escapes/:modalType?" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-escapes/:tab?', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/:tab?"><MyEscapesPage /></AuthenticatedRouteElement>} />
      {isShopEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/account/shop-orders/:tab?', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/shop-orders/:tab?"><ShopOrdersPage /></AuthenticatedRouteElement>} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/account/details/traveller', true)} element={<AuthenticatedRouteElement path="/:regionCode/account/details/traveller">{pageElement(AccountDetailsTravellerPage)}</AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/details', true)} element={isAgentHubEnabled ? <RedirectWithStatus code={302} exact from="/:regionCode/account/details" to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement showPopup path="/:regionCode/account/details"><AccountDetailsPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/booking-form/:orderId?', false)} element={<AuthenticatedRouteElement path="/:regionCode/booking-form/:orderId?"><TourTravellerInfoPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/review/:orderId?', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/review/:orderId?"><UserReviews /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/reset-password/:resetPasswordToken', true)} element={<TrackedRouteElement element={<ResetPasswordPage />} path="/:regionCode/reset-password/:resetPasswordToken" />} />
      {isLuxPlusEnabled && !isLoyaltyLuxplusLandingPageEnabled && <RouterRoute path={toPageRoutePath(`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`, true)} element={<LuxPlusMemberRoute showPopup end path={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} element={<LuxPlusAccountPage />} />} />}
      {isLuxPlusEnabled && isLoyaltyLuxplusLandingPageEnabled && <RouterRoute path={toPageRoutePath(`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`, false)} element={<RedirectWithStatus code={301} from={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} to={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} />} />}

      {/* hidden preference centre page */}
      <RouterRoute
        path={toPageRoutePath('/:regionCode/account/hidden-communication-preferences', false)}
        element={<AuthenticatedRouteElement
          showPopup
          path="/:regionCode/account/hidden-communication-preferences"
        >{

            config.BRAND === LUXURY_ESCAPES ? <MySubscriptionsPage /> : <MyWLSubscriptionsPage />
          }
        </AuthenticatedRouteElement>}
      />

      {/* Subscriptions Pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/resubscribe', true)} element={<TrackedRouteElement element={<ResubscribePage />} path="/:regionCode/resubscribe" />} />
      <RouterRoute
        path={toPageRoutePath('/:regionCode/account/communication-preferences/unsubscribe', true)}
        element={<TrackedRouteElement
          element={

            <MultiClickUnsubRouteElement />
          }
          path="/:regionCode/account/communication-preferences/unsubscribe"
        />}
      />

      <RouterRoute path={toPageRoutePath('/:regionCode/account/communication-preferences', false)} element={isAgentHubEnabled ? <RedirectWithStatus code={302} from="/:regionCode/account/communication-preferences" to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account/communication-preferences"><PreferenceCentrePage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/my-subscriptions', false)} element={<RedirectWithStatus code={301} from="/:regionCode/my-subscriptions" to="/:regionCode/account/communication-preferences" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/my-subscriptions', false)} element={<RedirectWithStatus code={301} from="/:regionCode/account/my-subscriptions" to="/:regionCode/account/communication-preferences" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/subscriptions', false)} element={<RedirectWithStatus code={301} from="/:regionCode/account/subscriptions" to="/:regionCode/account/communication-preferences" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account/payment-cards', true)} element={<RedirectWithStatus code={301} exact from="/:regionCode/account/payment-cards" to="/:regionCode/account/credits-payments" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/account-details', false)} element={<RedirectWithStatus code={301} from="/:regionCode/account-details" to="/:regionCode/account/details" />} />
      {/* Insurance pages */}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={toPageRoutePath('/:regionCode/travel-protection/quote', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/quote"><TravelInsuranceQuote /></AuthenticatedRouteElement>} />}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={toPageRoutePath('/:regionCode/travel-protection/add', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/add"><TravelInsuranceAddCheckout /></AuthenticatedRouteElement>} />}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={toPageRoutePath('/:regionCode/travel-protection/details', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/details"><TravelInsuranceDetails /></AuthenticatedRouteElement>} />}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={toPageRoutePath('/:regionCode/travel-protection/payment', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/payment">{pageElement(TravelInsurancePayment)}</AuthenticatedRouteElement>} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/passport-card-insurance/details', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/passport-card-insurance/details">{pageElement(PassportCardDetailsPage)}</AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/checkout', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/checkout" to="/:regionCode/travel-protection/quote" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/confirmation', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/confirmation" to="/:regionCode/booking/confirmation" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance" to="/:regionCode/travel-protection" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/xcover', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/xcover" to="/:regionCode/travel-protection/xcover" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/quote', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/quote" to="/:regionCode/travel-protection/quote" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/add', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/add" to="/:regionCode/travel-protection/add" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/details', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/details" to="/:regionCode/travel-protection/details" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/insurance/payment', false)} element={<RedirectWithStatus code={301} from="/:regionCode/insurance/payment" to="/:regionCode/travel-protection/payment" />} />
      {/* Gift card pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/giftcards', false)} element={<RedirectWithStatus code={301} from="/:regionCode/giftcards" to="/:regionCode/gift-cards" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/giftcard', false)} element={<RedirectWithStatus code={301} from="/:regionCode/giftcard" to="/:regionCode/gift-cards" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/gift-card', true)} element={<RedirectWithStatus code={301} from="/:regionCode/gift-card" to="/:regionCode/gift-cards" exact />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/gift-cards/confirmation', false)} element={<AuthenticatedRouteElement path="/:regionCode/gift-cards/confirmation"><GiftCardConfirmationPage /></AuthenticatedRouteElement>} />
      {/* Purchase credits page */}
      {isSpoofed && <RouterRoute path={toPageRoutePath('/:regionCode/purchase-credits', false)} element={<TrackedRouteElement element={<PurchaseCreditsPage />} path="/:regionCode/purchase-credits" />} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/gift-card/:code', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/gift-card/:code"><GiftCardViewPage /></AuthenticatedRouteElement>} />
      {/* Booking pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/booking/confirmation', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/booking/confirmation"><ConfirmationPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/bb/booking/confirmation', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/bb/booking/confirmation"><ConfirmationPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/confirmation/:orderId', false)} element={<RedirectWithStatus code={301} from="/:regionCode/confirmation/:orderId" to="/:regionCode/booking/confirmation?orderId=:orderId" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/bb/booking/confirmation', false)} element={<RedirectWithStatus code={301} from="/:regionCode/bb/booking/confirmation" to="/:regionCode/booking/confirmation" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/bb/booking/:step', false)} element={<RedirectWithStatus code={301} from="/:regionCode/bb/booking/:step" to="/:regionCode/booking/:step" />} />
      {/* Redeem gift hotel and experience */}
      <RouterRoute path={toPageRoutePath('/:regionCode/redeem/:redeemCode/:orderId/:offerId', true)} element={<TrackedRouteElement element={<RedeemGifted />} path="/:regionCode/redeem/:redeemCode/:orderId/:offerId" />} />
      {/* TourV2Pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/tour/:offerId', true)} element={<TrackedRouteElement element={<TourV2OfferPageElement />} path="/:regionCode/tour/:offerId" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/tour/:offerId/:variationId', true)} element={<TrackedRouteElement element={<TourV2OfferVariationPage />} path="/:regionCode/tour/:offerId/:variationId" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/search/tours', true)} element={<TrackedRouteElement element={<TourV2SearchPage />} path="/:regionCode/search/tours" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/tours/deals/:slug', true)} element={<TrackedRouteElement element={<TourDealPage />} path="/:regionCode/tours/deals/:slug" />} />
      {/* Tour signature series contentful redirects */}
      <RouterRoute path={toPageRoutePath('/:regionCode/signature-series.p1', false)} element={<RedirectWithStatus code={301} from="/:regionCode/signature-series.p1" to="/:regionCode/campaign/signature-series" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/indigo', false)} element={<RedirectWithStatus code={301} from="/:regionCode/indigo" to="/:regionCode/offer/hotel-indigo-melbourne-little-collins-victoria-australia/006RF00000MZ9gTYAT" />} />
      {/* Tailor-Made Tours */}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && <RouterRoute
        path={toPageRoutePath('/:regionCode/tailor-made-tours/requests/:requestId', true)}
        element={<TrackedRouteElement
          element={
            <TailorMadeTourRequestConfirmationPage />
          }
          path="/:regionCode/tailor-made-tours/requests/:requestId"
        />}
      />}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && <RouterRoute
        path={toPageRoutePath('/:regionCode/tailor-made-tours/requests', true)}
        element={<TrackedRouteElement
          element={
            <TailorMadeTourRequestPage />
          }
          path="/:regionCode/tailor-made-tours/requests"
        />}
      />}
      <RouterRoute path={toPageRoutePath('/:regionCode/tours/tours-catalogue', true)} element={<TrackedRouteElement element={<TourCataloguePage />} path="/:regionCode/tours/tours-catalogue" />} />
      {config.hasReferralProgram && <RouterRoute
        path={toPageRoutePath('/:regionCode/refer', true)}
        element={<TrackedRouteElement
          element={
            <ReferPage />
          }
          path="/:regionCode/refer"
        />}
      />}
      {config.hasReferralProgram && <RouterRoute
        path={toPageRoutePath('/:regionCode/refer/:referralCode', false)}
        element={<TrackedRouteElement
          element={
            <RefereeLandingPage />
          }
          path="/:regionCode/refer/:referralCode"
        />}
      />}
      <RouterRoute path={toPageRoutePath('/:regionCode/google-hotel-ads', false)} element={<TrackedRouteElement element={<GoogleHotelAdPage />} path="/:regionCode/google-hotel-ads" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/property/ayodya-resort-bali-bedbank:6486e2f1-958e-4f91-a5b0-481205904898', false)} element={<RedirectWithStatus code={302} from="/:regionCode/property/ayodya-resort-bali-bedbank:6486e2f1-958e-4f91-a5b0-481205904898" to="/:regionCode/offer/ayodya-resort-bali-nusa-dua-indonesia-asia/0062y00000JN0RvAAL" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/abercrombie-and-kent-tours.p1', false)} element={<RedirectWithStatus code={302} from="/:regionCode/abercrombie-and-kent-tours.p1" to="/:regionCode/tours" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/property/:propertyNameAndId', false)} element={<TrackedRouteElement element={<PropertyPage />} path="/:regionCode/property/:propertyNameAndId" />} />
      {config.ENABLE_AU_COMPETITION && <RouterRoute path={toPageRoutePath('/:regionCode/hunter-valley-competition', false)} element={<TrackedRouteElement element={<AuCompetitionsPage />} path="/:regionCode/hunter-valley-competition" />} />}
      {config.ENABLE_INTL_COMPETITION && <RouterRoute path={toPageRoutePath('/:regionCode/spin-the-wheel-competition', false)} element={<TrackedRouteElement element={<IntlCompetitionsPage />} path="/:regionCode/spin-the-wheel-competition" />} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/NHCollectionDubaiThePalm', false)} element={<TrackedRouteElement element={pageElement(ukCompetitionsPage)} path="/:regionCode/NHCollectionDubaiThePalm" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/citi', false)} element={<RedirectWithStatus code={301} from="/:regionCode/citi" to="/:regionCode/save-with-citi.p1" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/terms', false)} element={<RedirectWithStatus code={301} from="/:regionCode/terms" to="/:regionCode/terms-and-conditions" />} />
      <RouterRoute
        path={toPageRoutePath('/:regionCode/sharethis/verify', false)}
        element={<TrackedRouteElement
          element={

            <ShareThisVerifyRouteElement />
          }
          path="/:regionCode/sharethis/verify"
        />}
      />

      {/* Content hub pages */}
      {config.CONTENT_HUB_ENABLED && config.contentHub && <RouterRoute
        path={toPageRoutePath('/:regionCode/pages/:slug', false)}
        element={
          <RedirectWithStatus code={301} from="/:regionCode/pages/:slug" to="/:regionCode/:slug.p1" />
        }
      />}
      {config.CONTENT_HUB_ENABLED && config.contentHub && <RouterRoute
        path={toPageRoutePath('/:regionCode/:slug.p1', true)}
        element={<TrackedRouteElement
          element={
            <ContentHub />
          }
          path="/:regionCode/:slug.p1"
        />}
      />}
      {/* Support has a first-class route above; keep the static page config for navigation links only. */}
      {config.staticPages.filter(sp => sp.type !== 'support').map((sp) =>
        sp.url?.to && <RouterRoute

          key={sp.url.to}
          path={toPageRoutePath(
          `/:regionCode${sp.url.to}`, true)}
          element={<TrackedRouteElement
            element={
              <StaticPageRouteElement
                staticPage={sp}
                title={typeof sp.text === 'string' ? sp.text : intl.formatMessage(sp.text)}
              />
            }
            path={`/:regionCode${sp.url.to}`}
          />}
        />,

      )}
      {/* Promotional Pages */}
      {!standaloneFlightsEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/flights', false)} element={<TrackedRouteElement element={<FlightsLandingPage />} path="/:regionCode/flights" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/flights/:dealCategory?', true)} element={<TrackedRouteElement element={<FlightsSearchLandingPage />} path="/:regionCode/flights/:dealCategory?" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/flights-search', false)} element={<TrackedRouteElement element={<FlightsSearchPage />} path="/:regionCode/flights-search" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/flights-search-results', false)} element={<TrackedRouteElement element={<FlightsSearchResultsPage />} path="/:regionCode/flights-search-results" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={toPageRoutePath('/:regionCode/flights-search-results-multi-city', false)} element={<TrackedRouteElement element={pageElement(FlightsSearchMultiCityResultsPage)} path="/:regionCode/flights-search-results-multi-city" />} />}
      <RouterRoute path={toPageRoutePath('/:regionCode/flight/deal/:id', false)} element={<TrackedRouteElement element={<FlightDealLandingPage />} path="/:regionCode/flight/deal/:id" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/flights/campaigns/:id', false)} element={<TrackedRouteElement element={<FlightCampaignsLandingPage />} path="/:regionCode/flights/campaigns/:id" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/escapes-finder', false)} element={<TrackedRouteElement element={<EscapesFinderPage />} path="/:regionCode/escapes-finder" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/disneyland-offers', false)} element={<RedirectWithStatus code={301} from="/:regionCode/disneyland-offers" to="/:regionCode/experience/led%3A0062y00000Gui6KAAR" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/disneyland-offers-test', false)} element={<RedirectWithStatus code={301} from="/:regionCode/disneyland-offers-test" to="/:regionCode/experience/led%3A0062y000009EEOEAA4" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/spotify', false)} element={<RedirectWithStatus from="/:regionCode/spotify" to="/:regionCode/campaign/spotify" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/nyc', false)} element={<RedirectWithStatus from="/:regionCode/nyc" to="/:regionCode/campaign/new-york" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/traveltester', false)} element={<RedirectWithStatus from="/:regionCode/traveltester" to="/:regionCode/campaign/7news-traveltester" />} />
      <RouterRoute
        path="/:regionCode/friends/*"
        element={<LocaleAwareTrackedRoutes routes={friendsReferralRoutes} />}
      />

      {config.promotionalPages.map((pp) => {
        const PromotionalComponent = promotionalPages[pp.component]
        return <RouterRoute
          key={pp.url}
          path={toPageRoutePath(
            `/:regionCode${pp.url}`, false)}
          element={<TrackedRouteElement
            element={
              <PromotionalComponent />
            }
            path={`/:regionCode${pp.url}`}
          />}
        />
      })}
      {config.holidayPages && <RouterRoute
        path={toPageRoutePath('/:regionCode/holidays/:slug', false)}
        element={
          <RedirectWithStatus code={301} from="/:regionCode/holidays/:slug" to="/:regionCode/:slug.h1" />
        }
      />}
      {config.holidayPages && <RouterRoute
        path={toPageRoutePath('/:regionCode/:slug.h1', false)}
        element={<TrackedRouteElement
          element={
            <HolidayLandingPageElement />
          }
          path="/:regionCode/:slug.h1"
        />}
      />}
      {config.destinationPages && <RouterRoute
        path={toPageRoutePath('/:regionCode/:slug.d1', false)}
        element={
          <RedirectWithStatus code={301} from="/:regionCode/:slug.d1" to="/:regionCode/destination/:slug" />
        }
      />}
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-travel-awards-voting', false)} element={<RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting" to="/:regionCode/campaign/lux-list-travel-awards" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-travel-awards-voting-uk', false)} element={<RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting-uk" to="/:regionCode/campaign/lux-list-travel-awards-uk" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-travel-awards-voting-us', false)} element={<RedirectWithStatus code={301} from="/:regionCode/campaign/lux-list-travel-travel-awards-voting-us" to="/:regionCode/campaign/lux-list-travel-awards-us" />} />
      {/* Redirect due to wrong URL sent out */}
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/singapore/competition-april', false)} element={<RedirectWithStatus code={301} from="/:regionCode/campaign/singapore/competition-april" to="/:regionCode/campaign/competition-april" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-awards', true)} element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-awards-prizes', true)} element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-prizes" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-awards-us', true)} element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-us" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-awards-prizes-us', true)} element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-prizes-us" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-awards-uk', true)} element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-uk" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/lux-list-travel-awards-prizes-uk', true)} element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-prizes-uk" />} />
      <RouterRoute
        path={toPageRoutePath(

        `/:regionCode/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`, true)}
        element={<AuthenticatedRouteElement showPopup path={`/:regionCode/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`}><FriendsAndFamilyInviteRouteElement
          isEnabled={isFriendsAndFamilyPageEnabled}
          isStaff={isStaff}
        />
        </AuthenticatedRouteElement>}
      />

      <RouterRoute path={toPageRoutePath(`/:regionCode/campaign/${CHARTER_PAGE_SLUG}`, true)} element={<TrackedRouteElement element={<MaldivesDirectSearchPage />} path={`/:regionCode/campaign/${CHARTER_PAGE_SLUG}`} />} />
      <RouterRoute path={toPageRoutePath(`/:regionCode/campaign/${CHARTER_BEFORE_YOU_FLY_PAGE_SLUG}`, true)} element={<TrackedRouteElement element={<MaldivesDirectBeforeYouFlyPage />} path={`/:regionCode/campaign/${CHARTER_BEFORE_YOU_FLY_PAGE_SLUG}`} />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/destination/*', false)} element={<TrackedRouteElement element={<ContentfulWebPage />} path={'/:regionCode/destination/*'} />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/campaign/*', false)} element={<TrackedRouteElement element={<ContentfulWebPage />} path={'/:regionCode/campaign/*'} />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/country/maldives', false)} element={<RedirectWithStatus code={301} from="/:regionCode/hotels/country/maldives" to="/:regionCode/destination/maldives" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/country/bali', false)} element={<RedirectWithStatus code={301} from="/:regionCode/hotels/country/bali" to="/:regionCode/destination/bali" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/country/greece', false)} element={<RedirectWithStatus code={301} from="/:regionCode/hotels/country/greece" to="/:regionCode/destination/greece" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/country/spain', false)} element={<RedirectWithStatus code={301} from="/:regionCode/hotels/country/spain" to="/:regionCode/destination/spain" />} />
      {/* New URL Structure For SEO Destination/Product pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/:productType/country/:country', true)} element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/country/:country" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/:productType/city/:city/:state/:country', true)} element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/city/:city/:state/:country" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/:productType/city/:city/:country', true)} element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/city/:city/:country" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/:productType/location/:location/:city/:state/:country', true)} element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/location/:location/:city/:state/:country" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/:productType/location/:location/:city/:country', true)} element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/location/:location/:city/:country" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/:productType/location/:location/:country', true)} element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/location/:location/:country" />} />
      {/* HolidayPackage Pages: Rule of URL: [base]/holiday-packages/:location/:holidayType */}
      <RouterRoute path={toPageRoutePath('/:regionCode/holiday-packages/:location/:holidayType', true)} element={<TrackedRouteElement element={pageElement(DestinationLandingPage)} path="/:regionCode/holiday-packages/:location/:holidayType" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/holiday-packages/:location', true)} element={<TrackedRouteElement element={pageElement(DestinationLandingPage)} path="/:regionCode/holiday-packages/:location" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/resorts/:location', true)} element={<TrackedRouteElement element={pageElement(DestinationLandingPage)} path="/:regionCode/resorts/:location" />} />
      {/* SEO Destination/Product pages */}
      <RouterRoute path={toPageRoutePath('/:regionCode/:destinationProductDotRoute', true)} element={<TrackedRouteElement element={<DestinationProductDotRouteElement />} path="/:regionCode/:destinationProductDotRoute" />} />
      {config.LERE_ENABLE_TOP_PICKS_FOR_ME && <RouterRoute path={toPageRoutePath('/:regionCode/top-picks-for-me', false)} element={<TrackedRouteElement element={<TopPicksForMePage />} path="/:regionCode/top-picks-for-me" />} />}
      <RouterRoute
        path={toPageRoutePath('/:regionCode/ancillary-payment/:orderId/:type', false)}
        element={<AuthenticatedRouteElement
          showPopup
          path="/:regionCode/ancillary-payment/:orderId/:type"
        >{

            pageElement(AncillaryPaymentPage)
          }
        </AuthenticatedRouteElement>}
      />

      <RouterRoute
        path={toPageRoutePath('/:regionCode/ancillary-change-reservation/:orderId/:type/:itemId?', false)}
        element={<AuthenticatedRouteElement
          showPopup
          path="/:regionCode/ancillary-change-reservation/:orderId/:type/:itemId?"
        >{

            pageElement(AncillaryChangeReservationPage)
          }
        </AuthenticatedRouteElement>}
      />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/bnbl/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/bnbl/order/:orderId/item/:itemId"><RedirectCheckoutBNBL /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/change-dates/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/change-dates/order/:orderId/item/:itemId"><RedirectCheckoutChangeDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/confirm-dates/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/confirm-dates/order/:orderId/item/:itemId"><RedirectCheckoutConfirmDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/extend-dates/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/extend-dates/order/:orderId/item/:itemId"><RedirectCheckoutExtendDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/add-extra-guests/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/add-extra-guests/order/:orderId/item/:itemId"><RedirectCheckoutAddExtraGuests /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/upgrade-room/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/upgrade-room/order/:orderId/item/:itemId"><RedirectCheckoutRoomUpgrade /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/partner-room-upgrade/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/partner-room-upgrade/order/:orderId/item/:itemId"><RedirectCheckoutPartnerRoomUpgrade /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/flight-change-dates/order/:orderId/item/:itemId', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/flight-change-dates/order/:orderId/item/:itemId"><RedirectCheckoutFlightChangeDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout-restore-cart', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout-restore-cart"><CheckoutRestoreCartPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout-gateway', false)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout-gateway"><CheckoutGatewayPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/checkout/*', false)} element={<TrackedRouteElement element={<CheckoutPageRoutes />} path="/:regionCode/checkout/*" />} />
      {isTripPlannerRoute && <RouterRoute
        path="/:regionCode/trip-planner/*"
        element={
          <TripPlannerRoutes />
        }
      />}
      <RouterRoute path={toPageRoutePath('/:regionCode/cart', true)} element={<AuthenticatedRouteElement showPopup path="/:regionCode/cart"><UserCartPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/cart/s/:sharedCartId', true)} element={<AuthenticatedRouteElement accountAccessModalInitialMode="sharedCartInvite" showPopup path="/:regionCode/cart/s/:sharedCartId"><SharedCartInvitePage /></AuthenticatedRouteElement>} />
      <RouterRoute path={toPageRoutePath('/:regionCode/user-cart', false)} element={<RedirectWithStatus code={301} from="/:regionCode/user-cart" to="/:regionCode/cart" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/:holidayType', true)} element={<TrackedRouteElement element={<HolidayTypeLandingPage />} path="/:regionCode/hotels/:holidayType" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/:holidayType/:country', true)} element={<TrackedRouteElement element={<HolidayTypeLandingPage />} path="/:regionCode/hotels/:holidayType/:country" />} />
      <RouterRoute path={toPageRoutePath('/:regionCode/hotels/:holidayType/:country/:cityOrState', true)} element={<TrackedRouteElement element={<HolidayTypeLandingPage />} path="/:regionCode/hotels/:holidayType/:country/:cityOrState" />} />
      <RouterRoute
        path={toPageRoutePath('/:regionCode/orders/:orderId/accept-invite/:invitationId', true)}
        element={<TrackedRouteElement
          element={

            <AcceptOrderInvitePage />
          }
          path="/:regionCode/orders/:orderId/accept-invite/:invitationId"
        />}
      />

      {config.SECURE_PAYMENT_ENABLED && <RouterRoute
        path={toPageRoutePath('/:regionCode/pay/:paymentLinkId', true)}
        element={<TrackedRouteElement
          element={
            <SecurePaymentPage />
          }
          path="/:regionCode/pay/:paymentLinkId"
        />}
      />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount && <RouterRoute
        path={toPageRoutePath(
        `/:regionCode${LE_AGENT_HUB_SOCIETE_CONFIRMATION}`, true)}
        element={<TrackedRouteElement element={<SocieteConfirmationPage />} path={`/:regionCode${LE_AGENT_HUB_SOCIETE_CONFIRMATION}`} />}
      />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount && <RouterRoute
        path={toPageRoutePath(
        `/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`, true)}
        element={<TrackedRouteElement element={<AgentHubAccountSetupCompleted />} path={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`} />}
      />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount && <RouterRoute
        path={toPageRoutePath(

        `/:regionCode/${agentHubOrSubBrandConfig.basePath}/complete-account-setup`, false)}
        element={<RedirectWithStatus
          from={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/complete-account-setup`}
          to={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`}
        />}
      />}
      {config.SPOOFING_SEND_CONFIRMATION_EMAIL && <RouterRoute path={toPageRoutePath('/:regionCode/skycheck', false)} element={<TrackedRouteElement element={<SkyCheckConfirmationPage />} path="/:regionCode/skycheck" />} />}
      {isShopEnabled && isShopLocale && isMerchandiseRoute &&
        <>
          <RouterRoute
            path="/:regionCode/shop/*"
            element={
              <>
                <LocaleAwareTrackedRoutes routes={merchandiseShopNavRoutes} />
                <LocaleAwareTrackedRoutes routes={merchandiseShopPageRoutes} />
              </>
            }
          />
          <RouterRoute
            path="/:regionCode/search/shop/*"
            element={
              <>
                <LocaleAwareTrackedRoutes routes={merchandiseSearchNavRoutes} />
                <LocaleAwareTrackedRoutes routes={merchandiseSearchPageRoutes} />
              </>
            }
          />
        </>}
      {/* Mobile app marketing url, redirect this to home page until we have one */}
      <RouterRoute path={toPageRoutePath('/:regionCode/app', false)} element={<RedirectWithStatus code={302} from="/:regionCode/app" to="/:regionCode/" />} />
      <RouterRoute
        path={toPageRoutePath('*', false)}
        element={<TrackedRouteElement
          element={
            <HttpStatus code={404}>
              <Error404PageWithOffers />
            </HttpStatus>
          }
          path="*"
        />}
      />

    </RouterRoutes>)
}

function mapStateToProps(state: App.State) {
  return {
    currentRegionCode: state.geo.currentRegionCode,
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state) || isFlightsCredit(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
}

export default connect(mapStateToProps)(PageRoutes)
